import { notifications } from '@mantine/notifications';
import { CityPipelineRunRO } from '@routellers/types/dist/shared/city-pipeline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';

export const useCityPipeline = () => {
    const { t } = useTranslation();
    const [pipeline, setPipeline] = useState<CityPipelineRunRO | null>(null);
    const [autolinkLoading, setAutolinkLoading] = useState(false);
    const [gapAuditLoading, setGapAuditLoading] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

    const watchPipeline = (run: CityPipelineRunRO) => {
        setPipeline(run);
        clearInterval(pollRef.current);

        if (run.status !== 'running') return;

        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.cityAttractionsApi.pipelineStatus(run.id);

                if (!data) return;
                setPipeline(data);

                if (data.status !== 'running') {
                    clearInterval(pollRef.current);

                    const summary = (data.log || [])
                        .map((l) => `${l.stage}: ${l.error ? `FAILED (${l.error})` : l.summary}`)
                        .join('\n');

                    if (data.status === 'done') {
                        notifications.show({
                            color: 'green',
                            title: t('pipeline.processedTitle', { city: data.city }),
                            message: summary || t('pipeline.done'),
                            autoClose: 8000,
                        });
                    } else {
                        notifications.show({
                            color: 'red',
                            title: t('pipeline.stoppedTitle'),
                            message: data.error || summary || t('pipeline.unknownError'),
                            autoClose: 10000,
                        });
                    }
                }
            } catch {
                // retry next tick
            }
        }, 7000);
    };

    useEffect(() => {
        api.cityAttractionsApi
            .latestPipeline()
            .then(({ data }) => {
                if (data?.status === 'running') watchPipeline(data);
            })
            .catch(() => undefined);

        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startPipeline = async (cityId: number) => {
        const withContent = window.confirm(t('pipeline.contentConfirm'));

        try {
            const { data } = await api.cityAttractionsApi.startPipeline({
                data: { cityId, withContent, withEnglish: false },
            });

            if (!data) return;
            notifications.show({
                color: 'green',
                message: t('pipeline.started', { id: data.id, city: data.city }),
            });
            watchPipeline(data);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    const stopPipeline = async () => {
        if (!pipeline) return;

        if (!window.confirm(t('pipeline.stopConfirm', { id: pipeline.id, city: pipeline.city })))
            return;

        try {
            const { data } = await api.cityAttractionsApi.cancelPipeline(pipeline.id);

            if (data) setPipeline(data);
            clearInterval(pollRef.current);
            notifications.show({ color: 'green', message: t('pipeline.cancelled') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    const autolinkCity = async (cityId: number) => {
        if (!window.confirm(t('pipeline.autolinkConfirm', { cityId }))) {
            return;
        }

        setAutolinkLoading(true);

        try {
            const { data } = await api.cityPeopleApi.autolink({ data: { cityId } });

            notifications.show({
                color: 'green',
                message: t('pipeline.autolinkResult', {
                    created: data?.linksCreated ?? 0,
                    already: data?.alreadyLinked ?? 0,
                    persons: data?.personsScanned ?? 0,
                    attractions: data?.attractionsScanned ?? 0,
                }),
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setAutolinkLoading(false);
        }
    };

    const auditGaps = async (cityId: number) => {
        if (!window.confirm(t('pipeline.gapAuditConfirm', { cityId }))) return;

        setGapAuditLoading(true);

        try {
            const { data } = await api.cityAttractionsApi.gapAudit({ data: { cityId } });
            const names = (status: string) =>
                data.items
                    .filter((item) => item.status === status)
                    .map((item) => item.ruTitle || item.title)
                    .join(', ');

            notifications.show({
                color: 'green',
                title: t('pipeline.gapAuditTitle', { city: data.city }),
                message: [
                    t('pipeline.gapAuditResult', {
                        listed: data.listed,
                        existing: data.existing,
                        created: data.created,
                        unverified: data.unverified,
                        skipped: data.skipped,
                    }),
                    data.created ? `${t('pipeline.gapAuditCreated')}: ${names('created')}` : null,
                    data.unverified
                        ? `${t('pipeline.gapAuditUnverified')}: ${names('unverified')}`
                        : null,
                ]
                    .filter(Boolean)
                    .join('\n'),
                autoClose: false,
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setGapAuditLoading(false);
        }
    };

    const running = pipeline?.status === 'running';

    return {
        pipeline,
        running,
        autolinkLoading,
        gapAuditLoading,
        startPipeline,
        stopPipeline,
        autolinkCity,
        auditGaps,
    };
};
